import React, { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import BusinessCard from "./common/BusinessCard";
import { createGSAPContext } from "../hooks/useGSAPCleanup";
import "./Section5Closing.css";

gsap.registerPlugin(ScrollTrigger);

function Section5Closing() {
  const sectionRef = useRef(null);
  const cardRef = useRef(null);
  const backCardRef = useRef(null);
  const sendonCardRef = useRef(null);
  const sendonBackCardRef = useRef(null);
  const logoRef = useRef(null);
  const sendonLogoRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    const card = cardRef.current;
    const backCard = backCardRef.current;
    const sendonCard = sendonCardRef.current;
    const sendonBackCard = sendonBackCardRef.current;

    if (!section) return;

    const ctx = createGSAPContext(() => {
      // Main timeline for closing sequence
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: "top 80%",
          toggleActions: "play none none reverse",
        },
      });

      // Opening message animation
      tl.from(".closing-intro", {
        opacity: 0,
        y: 50,
        duration: 1.5,
        ease: "power3.out",
      })
        .from(
          ".closing-title",
          {
            opacity: 0,
            scale: 0.8,
            duration: 1.2,
            ease: "power2.out",
          },
          "-=0.5"
        )
        .from(
          ".closing-subtitle",
          {
            opacity: 0,
            y: 30,
            duration: 1,
          },
          "-=0.5"
        );

      // Simple fade-in animation for cards section
      const cardTl = gsap.timeline({
        scrollTrigger: {
          trigger: ".closing-card-section",
          start: "top 85%",
          toggleActions: "play none none reverse",
        },
      });

      cardTl
        .from(".card-section-title", {
          opacity: 0,
          y: 20,
          duration: 1,
          ease: "power2.out",
        })
        .from(
          ".card-pair-label",
          {
            opacity: 0,
            duration: 0.8,
            stagger: 0.2,
            ease: "power2.out",
          },
          "-=0.5"
        )
        .from(
          [card, backCard, sendonCard, sendonBackCard],
          {
            opacity: 0,
            y: 30,
            duration: 1,
            ease: "power2.out",
            stagger: 0.1,
          },
          "-=0.5"
        );

      // Final message animation
      const finalTl = gsap.timeline({
        scrollTrigger: {
          trigger: ".closing-final",
          start: "top 90%",
          toggleActions: "play none none reverse",
        },
      });

      finalTl
        .from(".final-message", {
          opacity: 0,
          scale: 0.95,
          duration: 1.5,
          ease: "power2.out",
        })
        .from(
          ".cta-message",
          {
            opacity: 0,
            y: 20,
            duration: 1,
          },
          "-=0.5"
        )
        .from(
          ".signature",
          {
            opacity: 0,
            duration: 1.5,
            ease: "power2.out",
          },
          "-=0.5"
        );

      // Logo fade overlay animation - triggered by scroll after "이 로고는 가려지기 위해 존재합니다"
      const logoFadeTl = gsap.timeline({
        scrollTrigger: {
          trigger: ".closing-main",
          start: "bottom 60%",
          end: "bottom top",
          scrub: true,
        },
      });

      // Animate both logo overlays in sync - with null check
      if (logoRef.current && sendonLogoRef.current) {
        logoFadeTl
          .fromTo(
            [logoRef.current, sendonLogoRef.current],
            {
              opacity: 0,
              x: -100,
            },
            {
              opacity: 0.85,
              x: 0,
              duration: 1,
              ease: "power2.inOut",
              stagger: 0.1,
            }
          )
          .to([logoRef.current, sendonLogoRef.current], {
            opacity: 0.85,
            duration: 0.5,
          })
          .to([logoRef.current, sendonLogoRef.current], {
            opacity: 0,
            x: 100,
            duration: 1,
            ease: "power2.inOut",
            stagger: 0.1,
          });
      }
    }, section);

    return () => {
      if (ctx && ctx.cleanup) {
        ctx.cleanup();
      }
    };
  }, []);

  return (
    <section className="section section-closing" ref={sectionRef}>
      <div className="container">
        <div className="closing-intro">
          <p className="intro-text">이것이 '알리는사람들'입니다.</p>
        </div>

        <div className="closing-main">
          <h2 className="closing-title">
            로고를 가림으로써
            <br />더 강하게 각인되는 브랜드
          </h2>
          <p className="closing-subtitle">
            고객을 앞세우는 것이 우리의 철학입니다
          </p>
        </div>

        <div className="closing-card-section">
          <h3 className="card-section-title">
            디자인 예시
          </h3>
          <div className="cards-wrapper">
            <div className="card-set aligo-set">
              <div className="card-pair-label">ALIGO</div>
              <div className="card-pair">
                <div className="card-container front-card" ref={cardRef}>
                  <BusinessCard
                    name="박대성"
                    team="테크팀"
                    phone="010-6533-1985"
                    email="pds@alipeople.kr"
                    showLogo={true}
                    className="closing-card"
                  />
                </div>
                <div className="card-container back-card" ref={backCardRef}>
                  <div className="card-back aligo-back">
                    <img
                      src="/assets/logos/aligo_alternate_positive.png"
                      alt="ALIGO"
                      className="card-back-logo aligo-logo"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="card-set sendon-set">
              <div className="card-pair-label">SENDON</div>
              <div className="card-pair">
                <div className="card-container front-card" ref={sendonCardRef}>
                  <BusinessCard
                    name="박대성"
                    team="테크팀"
                    phone="010-6533-1985"
                    email="pds@alipeople.kr"
                    showLogo={true}
                    logoSrc="/assets/logos/sendon_alternate_positive.png"
                    logoAlt="SendOn"
                    className="closing-card sendon-card"
                  />
                </div>
                <div
                  className="card-container back-card"
                  ref={sendonBackCardRef}
                >
                  <div className="card-back sendon-back">
                    <img
                      src="/assets/logos/sendon_alternate_positive.png"
                      alt="SendOn"
                      className="card-back-logo sendon-logo"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="closing-final">
          <h3 className="final-message">
            이 명함이 우리의 정체성을
            <br />
            매일 상기시킬 것입니다
          </h3>

          <p className="cta-message">
            우리는 계속해서 고객을 알리는 사람들이 될 것입니다.
            <br />
            그것이 우리가 존재하는 이유입니다.
          </p>

          <div className="signature">
            <span className="company-name">알리는사람들</span>
            <span className="tagline">Customer First, Always</span>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Section5Closing;
